import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { today, makeTaskComment } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES } from '../../core/constants';
import { Icon } from '../../components/Icon';


function EditTaskModal({ data, onClose }) {
  const { employees, tasks, setTasks, user, showToast, runSubmitting } = useApp();
  const task = data?.task;
  const [form, setForm] = useState({
    title:      task?.title || "",
    assignedTo: task?.assignedTo || "",
    dueDate:    task?.dueDate || "",
    category:   task?.category || "General",
    status:     task?.status || "open",
    allowClientEdit: task?.allowClientEdit || false,
  });
  const [comments, setComments] = useState(() => [...(task?.comments || [])]);
  const [newNote, setNewNote] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!task) return null;

  const addNote = () => {
    const entry = makeTaskComment(user, newNote);
    if (!entry) return;
    setComments(prev => [...prev, entry]);
    setNewNote("");
  };

  const save = async () => {
    if (!form.title) { showToast("Title required", "error"); return; }

    let nextComments = comments;
    const pending = makeTaskComment(user, newNote);
    if (pending) nextComments = [...comments, pending];

    const legacy = String(task.notes || "").trim();
    if (legacy && !nextComments.some(c => c.text === legacy)) {
      const migrated = makeTaskComment(user, legacy);
      if (migrated) nextComments = [...nextComments, migrated];
    }

    await runSubmitting('Saving task…', async () => {
      await sb.update("tasks", "id", task.id, {
        title:       form.title,
        assigned_to: form.assignedTo || null,
        due_date:    form.dueDate || null,
        category:    form.category,
        status:      form.status,
        allow_client_edit: form.allowClientEdit,
        comments:    nextComments,
        notes:       "",
        completed_date: form.status === "completed" ? new Date().toISOString().split("T")[0] : null,
        updated_at:  new Date().toISOString(),
      });

      setTasks(ts => ts.map(t => t.id === task.id ? {
        ...t,
        ...form,
        comments: nextComments,
        notes: "",
        completedDate: form.status === "completed" ? today() : t.completedDate,
      } : t));

      showToast("Task updated!", "success");
      onClose();
    }, 'task');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 520 }}>
      <div className="modal-head">
        <div>
          <div className="modal-title">Edit Task</div>
          <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>{task.clientName}</div>
        </div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="modal-body">
        <div className="form-grid">
          <div className="f-group">
            <label className="f-label">Task Title <span className="f-req">*</span></label>
            <input className="f-input" value={form.title} onChange={e=>set("title",e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Status</label>
            <select className="f-select" value={form.status} onChange={e=>set("status",e.target.value)}>
              {TASK_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Assign To</label>
            <select className="f-select" value={form.assignedTo} onChange={e=>set("assignedTo",e.target.value)}>
              <option value="">Unassigned</option>
              {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Due Date</label>
            <input className="f-input" type="date" value={form.dueDate} onChange={e=>set("dueDate",e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Category</label>
            <select className="f-select" value={form.category} onChange={e=>set("category",e.target.value)}>
              {["General","GST","ROC","Income Tax","TDS","Audit","DSC","Trademark","MSME","Other"].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Team Notes</label>
            <div className="f-hint" style={{ marginBottom: 8 }}>Visible to the client in My Tasks.</div>
            {comments.length === 0 && !task.notes && (
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>No notes yet.</div>
            )}
            {comments.map(c => (
              <div key={c.id} style={{ padding: "8px 10px", background: "#F9FAFB", borderRadius: 8, marginBottom: 6, border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{c.userName} · {c.at}</div>
                <div style={{ fontSize: 13 }}>{c.text}</div>
              </div>
            ))}
            {task.notes && !comments.some(c => c.text === task.notes) && (
              <div style={{ padding: "8px 10px", background: "#FFFBEB", borderRadius: 8, marginBottom: 8, border: "1px solid #FDE68A", fontSize: 13 }}>
                {task.notes}
                <div className="f-hint" style={{ marginTop: 4 }}>Legacy note — add a new note below to sync to client.</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <textarea className="f-textarea" rows={2} style={{ flex: 1 }} placeholder="Add a note for the client…" value={newNote} onChange={e => setNewNote(e.target.value)} />
              <button type="button" className="btn btn-primary btn-sm" onClick={addNote}>Add</button>
            </div>
          </div>
          <div className="f-group">
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13 }}>
              <input type="checkbox" checked={form.allowClientEdit} onChange={e=>set("allowClientEdit", e.target.checked)} />
              <span>Allow client to edit / re-submit this task</span>
            </label>
            <div className="f-hint">When on, the client can update this task from their portal even if it isn't in "Client Action" status.</div>
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Save Changes</button>
      </div>
    </div>
  );
}

export { EditTaskModal };
